import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import ExportDropdown from "./ExportDropdown";
import { formatPrice } from "@/utils/formatPrice";
import type { ReportDateRange } from "./ReportDateRangePicker";

interface BookingRecord {
  status: string;
  court_id: string;
  start_time: string;
  duration_minutes: number | null;
  price_total: number | null;
  [key: string]: unknown;
}

interface Props {
  range: ReportDateRange;
  bookings: BookingRecord[];
  prevBookings: BookingRecord[];
  courtCount: number;
  hallHours: { start: number; end: number } | null;
  courts: { id: string; name: string }[];
  compareEnabled: boolean;
  payments: { amount: number; [key: string]: unknown }[];
}

export default function OccupancyTab({ range, bookings, prevBookings, courtCount, hallHours, courts, compareEnabled, payments: _payments }: Props) {
  const { t } = useTranslation("admin");
  const [courtFilter, setCourtFilter] = useState("all");

  const filteredBookings = useMemo(() => {
    const confirmed = bookings.filter((b) => b.status !== "cancelled");
    if (courtFilter === "all") return confirmed;
    return confirmed.filter((b) => b.court_id === courtFilter);
  }, [bookings, courtFilter]);

  const filteredPrev = useMemo(() => {
    const confirmed = prevBookings.filter((b) => b.status !== "cancelled");
    if (courtFilter === "all") return confirmed;
    return confirmed.filter((b) => b.court_id === courtFilter);
  }, [prevBookings, courtFilter]);

  const days = Math.max(1, Math.round((range.to.getTime() - range.from.getTime()) / 86400000) + 1);
  const hoursPerDay = hallHours ? (hallHours.end - hallHours.start) : 14;
  const effectiveCourts = courtFilter === "all" ? courtCount : 1;
  const totalSlots = effectiveCourts * hoursPerDay * days;

  const totalHours = Math.round(filteredBookings.reduce((s: number, b) => s + (b.duration_minutes || 0), 0) / 60);
  const prevTotalHours = Math.round(filteredPrev.reduce((s: number, b) => s + (b.duration_minutes || 0), 0) / 60);
  const occupancy = totalSlots > 0 ? Math.round((totalHours / totalSlots) * 100) : 0;
  const prevOccupancy = totalSlots > 0 ? Math.round((prevTotalHours / totalSlots) * 100) : 0;

  // Heatmap data
  const heatmapData = useMemo(() => {
    const dayNames = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
    const startH = hallHours?.start ?? 6;
    const endH = hallHours?.end ?? 23;
    const grid: { day: string; hour: number; count: number }[] = [];
    for (let d = 0; d < 7; d++) {
      for (let h = startH; h < endH; h++) {
        const count = filteredBookings.filter((b) => {
          const dt = new Date(b.start_time);
          const dow = (dt.getDay() + 6) % 7;
          return dow === d && dt.getHours() === h;
        }).length;
        grid.push({ day: dayNames[d], hour: h, count });
      }
    }
    return grid;
  }, [filteredBookings, hallHours]);

  const heatMax = Math.max(1, ...heatmapData.map((h) => h.count));
  const startH = hallHours?.start ?? 6;
  const endH = hallHours?.end ?? 23;
  const hourCount = endH - startH;

  // Hourly distribution
  const hourlyData = useMemo(() => {
    const map: Record<number, number> = {};
    filteredBookings.forEach((b) => {
      const h = new Date(b.start_time).getHours();
      map[h] = (map[h] || 0) + 1;
    });
    return Object.entries(map).map(([h, c]) => ({ hour: `${h}:00`, count: c })).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  }, [filteredBookings]);

  // RevPAH per court
  const revpahData = useMemo(() => {
    const availableHours = days * hoursPerDay;
    return courts.map((court) => {
      const revenue = filteredBookings
        .filter((b) => b.court_id === court.id)
        .reduce((sum, b) => sum + (b.price_total ?? 0), 0);
      const revpah = availableHours > 0 ? revenue / availableHours : 0;
      return { id: court.id, name: court.name, revenue, availableHours, revpah };
    });
  }, [filteredBookings, courts, days, hoursPerDay]);

  const totalRevpah = useMemo(() => {
    const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.price_total ?? 0), 0);
    const totalAvailable = days * hoursPerDay * effectiveCourts;
    return totalAvailable > 0 ? totalRevenue / totalAvailable : 0;
  }, [filteredBookings, days, hoursPerDay, effectiveCourts]);

  // Prime time fill rate (weekdays 17-21)
  const primeTimeFillRate = useMemo(() => {
    const primeBookings = filteredBookings.filter((b) => {
      const dt = new Date(b.start_time);
      const dow = dt.getDay(); // 0=Sun, 1=Mon ... 5=Fri, 6=Sat
      const hour = dt.getHours();
      return dow >= 1 && dow <= 5 && hour >= 17 && hour < 21;
    });
    // Count weekdays in range
    let weekdayCount = 0;
    const cursor = new Date(range.from);
    cursor.setHours(0, 0, 0, 0);
    const endDate = new Date(range.to);
    endDate.setHours(23, 59, 59, 999);
    while (cursor <= endDate) {
      const dow = cursor.getDay();
      if (dow >= 1 && dow <= 5) weekdayCount++;
      cursor.setDate(cursor.getDate() + 1);
    }
    const totalPrimeSlots = weekdayCount * 4 * effectiveCourts;
    const rate = totalPrimeSlots > 0 ? Math.round((primeBookings.length / totalPrimeSlots) * 100) : 0;
    return { count: primeBookings.length, totalSlots: totalPrimeSlots, rate };
  }, [filteredBookings, range, effectiveCourts]);

  // Prisinsikter (pricing insights)
  const pricingInsights = useMemo(() => {
    const maxCount = Math.max(1, ...heatmapData.map((h) => h.count));
    const lowThreshold = maxCount * 0.3;
    const highThreshold = maxCount * 0.9;
    const suggestions: string[] = [];

    const lowSlots = heatmapData.filter((h) => h.count > 0 && h.count < lowThreshold);
    const highSlots = heatmapData.filter((h) => h.count >= highThreshold);

    if (lowSlots.length > 0) {
      const grouped = lowSlots.reduce<Record<string, number[]>>((acc, s) => {
        (acc[s.day] ??= []).push(s.hour);
        return acc;
      }, {});
      for (const [day, hours] of Object.entries(grouped)) {
        const hourRange = `${Math.min(...hours)}:00–${Math.max(...hours) + 1}:00`;
        suggestions.push(`${day} ${hourRange}: Låg beläggning — överväg lägre pris för att öka bokning.`);
      }
    }

    if (highSlots.length > 0) {
      const grouped = highSlots.reduce<Record<string, number[]>>((acc, s) => {
        (acc[s.day] ??= []).push(s.hour);
        return acc;
      }, {});
      for (const [day, hours] of Object.entries(grouped)) {
        const hourRange = `${Math.min(...hours)}:00–${Math.max(...hours) + 1}:00`;
        suggestions.push(`${day} ${hourRange}: Hög beläggning — överväg högre pris (premium-tid).`);
      }
    }

    if (suggestions.length === 0) {
      suggestions.push("Inga tydliga prisinsikter just nu — beläggningen är jämnt fördelad.");
    }

    return suggestions;
  }, [heatmapData]);

  const exportData = () => heatmapData.map((h) => ({ [t("reports.occupancy.day")]: h.day, [t("reports.occupancy.hour")]: h.hour, [t("reports.occupancy.bookings")]: h.count }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Select value={courtFilter} onValueChange={setCourtFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("reports.occupancy.allCourts")}</SelectItem>
            {courts.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ExportDropdown getData={exportData} filePrefix="beläggning" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.occupancy.avgOccupancy")}</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold">{occupancy}%</p>
              {compareEnabled && (
                occupancy >= prevOccupancy
                  ? <TrendingUp className="h-5 w-5 text-green-500" />
                  : <TrendingDown className="h-5 w-5 text-destructive" />
              )}
            </div>
            {compareEnabled && <p className="text-xs text-muted-foreground">{t("reports.occupancy.previous")}: {prevOccupancy}%</p>}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.occupancy.playedHours")}</p>
            <p className="text-2xl font-bold">{totalHours}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.occupancy.bookingCount")}</p>
            <p className="text-2xl font-bold">{filteredBookings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Heatmap */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.occupancy.heatmapTitle")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="overflow-x-auto">
            <div className="grid gap-0.5" style={{ gridTemplateColumns: `auto repeat(${hourCount}, 1fr)` }}>
              <div />
              {Array.from({ length: hourCount }, (_, i) => (
                <div key={i} className="text-[10px] text-muted-foreground text-center">{i + startH}</div>
              ))}
              {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((day) => (
                <div key={day} className="contents">
                  <div className="text-[10px] text-muted-foreground pr-1 flex items-center">{day}</div>
                  {Array.from({ length: hourCount }, (_, i) => {
                    const h = i + startH;
                    const item = heatmapData.find((x) => x.day === day && x.hour === h);
                    const intensity = item ? item.count / heatMax : 0;
                    return (
                      <div
                        key={`${day}-${h}`}
                        className="aspect-square rounded-sm border border-border"
                        style={{ backgroundColor: `hsl(var(--primary) / ${Math.max(0.05, intensity)})` }}
                        title={`${day} ${h}:00 — ${item?.count || 0} bokningar`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hourly distribution */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.occupancy.popularTimes")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} name={t("reports.occupancy.bookings")} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* RevPAH */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total RevPAH</p>
          <p className="text-2xl font-bold">{formatPrice(Math.round(totalRevpah))}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.occupancy.revpahPerCourt")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("reports.occupancy.court")}</TableHead>
                <TableHead className="text-right">{t("reports.occupancy.revenue")}</TableHead>
                <TableHead className="text-right">{t("reports.occupancy.hours")}</TableHead>
                <TableHead className="text-right">RevPAH</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {revpahData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell className="text-right">{formatPrice(Math.round(row.revenue))}</TableCell>
                  <TableCell className="text-right">{row.availableHours}h</TableCell>
                  <TableCell className="text-right">{formatPrice(Math.round(row.revpah))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Prime time fill rate */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.occupancy.primeTimeOccupancy")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <p className="text-3xl font-bold">{primeTimeFillRate.rate}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t("reports.occupancy.primeTimeSlots", { count: primeTimeFillRate.count, total: primeTimeFillRate.totalSlots })}
          </p>
        </CardContent>
      </Card>

      {/* Prisinsikter */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.occupancy.pricingInsights")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <ul className="space-y-1 text-sm">
            {pricingInsights.map((insight, i) => (
              <li key={i} className="text-muted-foreground">• {insight}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
